import React, { useEffect, useState } from 'react';

import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Toggle from '@amzn/meridian/toggle';

import { isEqual } from 'lodash';
import { useLocation } from 'react-router-dom';
import { PaperPlanSubscriptionProduct } from 'src/constants';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import {
    ALPHANUMERIC_SPACE_ONLY,
    ALPHANUMERIC_UNDERSCORES_ONLY,
    AT_LEAST_5_CHAR,
    AT_MOST_49_CHAR,
    AT_MOST_99_CHAR,
    INVALID_REPORTING_NAME_CHARACTERS,
    IS_REQUIRED
} from 'src/constants/errorMessages';
import { DEFAULT_PROMO_AFFILIATE_NAME, defaultPlanValues } from 'src/constants/paperPlans';
import { ROUTES } from 'src/constants/routes';
import { IPlanProps, IPlanStrValues } from 'src/types/IPaperPlan';
import {
    validateAlphanumericAndSpace,
    validateAlphanumericAndUnderscore,
    validateMaxLength,
    validateMinLength,
    validateReportingNameCharacters,
    validateRequired
} from 'src/util/validationsUtil';

import './PlanSettings.scss';

export const PlanSettings = ({ onNext, onCancel, planValues, handlePlanValuesChange, setPlanValues }: IPlanProps) => {
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);
    const { pathname } = useLocation();
    const isNewPaperPlan = pathname.includes(`${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.ADD}`);

    const [isCarrierConfirmOpen, setCarrierConfirmOpen] = useState(false);

    const onChangeCarrier = (newVal: boolean) => {
        if (newVal) {
            setCarrierConfirmOpen(true);
        } else {
            handlePlanValuesChange('carrierPlan', false);
        }
    };

    const onChangeCarrierConfirm = () => {
        setCarrierConfirmOpen(false);
        handlePlanValuesChange('carrierPlan', true);
        handlePlanValuesChange('affiliateName', '');
        setErrorMessages((prevState) => ({ ...prevState, affiliateName: IS_REQUIRED }));
    };

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [planValues, errorMessages]);

    useEffect(() => {
        if (!isNewPaperPlan) return;

        if (planValues.employeePlan) {
            setPlanValues((prevState) => ({
                ...prevState,
                carrierPlan: false,
                royaltyBearing: false,
                customerPays: false
            }));
        } else if (!planValues.royaltyBearing) {
            setPlanValues((prevState) => ({
                ...prevState,
                customerPays: false
            }));
        } else if (planValues.carrierPlan && planValues.territory === GLOBAL_TERRITORY.NAME) {
            setPlanValues((prevState) => ({
                ...prevState,
                territory: ''
            }));
        } else if (!planValues.carrierPlan) {
            setPlanValues((prevState) => ({
                ...prevState,
                territory: GLOBAL_TERRITORY.NAME,
                affiliateName: DEFAULT_PROMO_AFFILIATE_NAME
            }));
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                if (newState['affiliateName']) delete newState['affiliateName'];
                return newState;
            });
        }
    }, [planValues.employeePlan, planValues.royaltyBearing, planValues.carrierPlan]);

    const checkFormValidity = () => {
        const requiredFields = [
            'paperPlanName',
            'displayName',
            'reportingName',
            'description',
            'subscriptionProduct',
            'affiliateName'
        ];

        for (const field of requiredFields) {
            if (!planValues[field as keyof IPlanStrValues]) {
                return false;
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(planValues, defaultPlanValues);
    };

    const onBlurField = (field: keyof IPlanStrValues) => {
        let errorMessage = '';
        const value = planValues[field];

        if (field === 'paperPlanName') {
            // TODO: add paperPlanName uniqueness check with API call
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateAlphanumericAndUnderscore(value)) {
                errorMessage = ALPHANUMERIC_UNDERSCORES_ONLY;
            } else if (!validateMinLength(value, 5)) {
                errorMessage = AT_LEAST_5_CHAR;
            } else if (!validateMaxLength(value, 99)) {
                errorMessage = AT_MOST_99_CHAR;
            }
        } else if (field === 'displayName') {
            // TODO: add displayName uniqueness check with API call
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateAlphanumericAndSpace(value)) {
                errorMessage = ALPHANUMERIC_SPACE_ONLY;
            } else if (!validateMinLength(value, 5)) {
                errorMessage = AT_LEAST_5_CHAR;
            } else if (!validateMaxLength(value, 49)) {
                errorMessage = AT_MOST_49_CHAR;
            }
        } else if (field === 'reportingName') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (value && !validateReportingNameCharacters(value)) {
                errorMessage = INVALID_REPORTING_NAME_CHARACTERS;
            }
        } else if (field === 'affiliateName') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (value && !validateAlphanumericAndSpace(value)) {
                errorMessage = ALPHANUMERIC_SPACE_ONLY;
            }
        } else if (field === 'description' || field === 'subscriptionProduct') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    return (
        <>
            <Heading level={1}>Plan settings</Heading>
            <Column spacingInset="500 none none none" spacing="400">
                <Input
                    label="PAPER plan ID *"
                    value={planValues.paperPlanName}
                    onChange={(newVal) => handlePlanValuesChange('paperPlanName', newVal)}
                    onBlur={() => onBlurField('paperPlanName')}
                    error={!!errorMessages.paperPlanName}
                    errorMessage={errorMessages.paperPlanName}
                    size="xlarge"
                />

                <Input
                    label="Display name *"
                    value={planValues.displayName}
                    onChange={(newVal) => handlePlanValuesChange('displayName', newVal)}
                    onBlur={() => onBlurField('displayName')}
                    error={!!errorMessages.displayName}
                    errorMessage={errorMessages.displayName}
                    size="xlarge"
                />

                <Input
                    label="External reporting name - This value will appear in external reports*"
                    value={planValues.reportingName}
                    onChange={(newVal) => handlePlanValuesChange('reportingName', newVal)}
                    onBlur={() => onBlurField('reportingName')}
                    error={!!errorMessages.reportingName}
                    errorMessage={errorMessages.reportingName}
                    size="xlarge"
                />

                <Select
                    label="Subscription product - This value will appear in external reports*"
                    value={planValues.subscriptionProduct}
                    onChange={(newVal) => handlePlanValuesChange('subscriptionProduct', newVal)}
                    onBlur={() => onBlurField('subscriptionProduct')}
                    error={!!errorMessages.subscriptionProduct}
                    errorMessage={errorMessages.subscriptionProduct}
                    size="xlarge"
                >
                    {Object.values(PaperPlanSubscriptionProduct).map((option) => (
                        <SelectOption key={option} label={option} value={option} />
                    ))}
                </Select>

                <Input
                    label="Promo affiliate name - This value will appear in external reports*"
                    value={planValues.affiliateName}
                    onChange={(newVal) => handlePlanValuesChange('affiliateName', newVal)}
                    onBlur={() => onBlurField('affiliateName')}
                    error={!!errorMessages.affiliateName}
                    errorMessage={errorMessages.affiliateName}
                    size="xlarge"
                />

                <Textarea
                    label="Description *"
                    value={planValues.description || ''}
                    onChange={(newVal) => handlePlanValuesChange('description', newVal)}
                    onBlur={() => onBlurField('description')}
                    error={!!errorMessages.description}
                    errorMessage={errorMessages.description}
                    resize="none"
                />

                <Row className="plan-setting-toggle-combo">
                    <Toggle
                        checked={planValues.employeePlan}
                        onChange={(newVal) => handlePlanValuesChange('employeePlan', newVal)}
                    />
                    <Text tag="label">Employee plan</Text>
                </Row>

                <Row className="plan-setting-toggle-combo">
                    <Toggle
                        disabled={planValues.employeePlan}
                        checked={planValues.carrierPlan}
                        onChange={onChangeCarrier}
                    />
                    <Text tag="label">Carrier plan</Text>
                    <Modal open={isCarrierConfirmOpen}>
                        <Column>
                            <Alert type={'warning'}>Carrier plans are a legacy feature.</Alert>
                            <Text>Are you sure you want to continue?</Text>
                            <Row>
                                <Button onClick={() => setCarrierConfirmOpen(false)}>No, go back</Button>
                                <Button type={'secondary'} onClick={onChangeCarrierConfirm}>
                                    Yes, create a carrier plan
                                </Button>
                            </Row>
                        </Column>
                    </Modal>
                </Row>

                <Row className="plan-setting-toggle-combo">
                    <Toggle
                        disabled={planValues.employeePlan}
                        checked={planValues.royaltyBearing}
                        onChange={(newVal) => handlePlanValuesChange('royaltyBearing', newVal)}
                    />
                    <Text tag="label">Royalty bearing</Text>
                </Row>

                <Row className="plan-setting-toggle-combo">
                    <Toggle
                        disabled={planValues.employeePlan || !planValues.royaltyBearing}
                        checked={planValues.customerPays}
                        onChange={(newVal) => handlePlanValuesChange('customerPays', newVal)}
                    />
                    <Text tag="label">Customer pays</Text>
                </Row>

                <Row className="plan-setting-toggle-combo">
                    <Toggle
                        checked={planValues.isBundle}
                        onChange={(newVal) => handlePlanValuesChange('isBundle', newVal)}
                    />
                    <Text tag="label">Is bundle</Text>
                </Row>

                <Column>
                    <Row alignmentHorizontal="end">
                        <Button type="link" onClick={onCancel} aria-label="Cancel plan">
                            Cancel
                        </Button>
                        <Button type="primary" disabled={!isFormValid} onClick={onNext} aria-label="Go to next step.">
                            Next
                        </Button>
                    </Row>
                </Column>
            </Column>
        </>
    );
};
